/**
 * Party archive / restore (SPEC screen 7).
 *
 * Archiving is a guarded transition, so the dialog opens by running the
 * contract's `dry_run=true` preflight (no body, no If-Match, still
 * Idempotency-Key). A blocked preflight answers 422 `party_archive_blocked`
 * with a closed `blockers[]` array, which renders as a checklist — never a
 * toast, because each blocker is an instruction, not a notification.
 *
 * The mutating archive then sends the non-PII reason code plus the party's
 * strong If-Match; `party_already_archived` / `party_not_archived` (409) are
 * stale-tab races that silently refresh instead of shouting at the operator.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ShieldCheck, X } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { ApiError } from "@/lib/api/errors";

import { ARCHIVE_REASON_CODES, type ArchiveReasonCode, type PartyArchiveBlocker } from "../api";

export const ARCHIVE_BLOCKED_CODE = "party_archive_blocked";
export const ALREADY_ARCHIVED_CODE = "party_already_archived";
export const NOT_ARCHIVED_CODE = "party_not_archived";

/**
 * `blockers[]` is a documented extension member of the partners problem, so
 * it is not on the shared `Problem` interface. Narrow it structurally rather
 * than trusting a cast: an unparsable blocker is dropped, never rendered.
 */
export function readBlockers(error: unknown): PartyArchiveBlocker[] {
  if (!(error instanceof ApiError)) return [];
  const raw = (error.problem as { blockers?: unknown } | undefined)?.blockers;
  if (!Array.isArray(raw)) return [];
  const blockers: PartyArchiveBlocker[] = [];
  for (const candidate of raw) {
    if (typeof candidate !== "object" || candidate === null) continue;
    const entry = candidate as Record<string, unknown>;
    if (entry.code === "active_orders" || entry.code === "stock_movements") {
      if (typeof entry.count === "number") {
        blockers.push({ code: entry.code, count: entry.count });
      }
      continue;
    }
    if (entry.code === "nonzero_balance" && typeof entry.balance === "object") {
      const balance = entry.balance as Record<string, unknown>;
      if (typeof balance.amount === "string" && typeof balance.currency === "string") {
        blockers.push({
          code: "nonzero_balance",
          balance: { amount: balance.amount, currency: balance.currency },
        });
      }
    }
  }
  return blockers;
}

export interface ArchiveDialogProps {
  open: boolean;
  partyName: string;
  /** Preflight in flight. */
  checking: boolean;
  /** True once a dry run (or a blocked archive attempt) reported blockers. */
  blockers: PartyArchiveBlocker[];
  /** Preflight itself failed (not a blocker list) — offer a retry. */
  guardError: boolean;
  archiving: boolean;
  onRetryGuard: () => void;
  onConfirm: (reasonCode: ArchiveReasonCode) => void;
  onOpenChange: (open: boolean) => void;
}

export function ArchiveDialog({
  open,
  partyName,
  checking,
  blockers,
  guardError,
  archiving,
  onRetryGuard,
  onConfirm,
  onOpenChange,
}: ArchiveDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<ArchiveReasonCode>("relationship_ended");

  useEffect(() => {
    if (open) setReason("relationship_ended");
  }, [open]);

  const blocked = blockers.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t("actions.close")} data-testid="partners-archive-dialog">
        <DialogHeader>
          <DialogTitle>
            {blocked
              ? t("partners.archive.blockedTitle", { name: partyName })
              : t("partners.archive.dialogTitle", { name: partyName })}
          </DialogTitle>
          <DialogDescription>
            {blocked ? t("partners.archive.blockedIntro") : t("partners.archive.consequences")}
          </DialogDescription>
        </DialogHeader>

        {checking ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner className="size-4" aria-hidden="true" />
            {t("partners.archive.checking")}
          </p>
        ) : guardError ? (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="size-4" aria-hidden="true" />
            <span>{t("partners.archive.guardError")}</span>
            <Button type="button" size="sm" variant="outline" onClick={onRetryGuard}>
              {t("actions.retry")}
            </Button>
          </div>
        ) : blocked ? (
          <ul className="flex flex-col gap-2" data-testid="partners-archive-blockers">
            {blockers.map((blocker) => (
              <li key={blocker.code} className="flex items-start gap-2 text-sm">
                <X className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
                <span>
                  {blocker.code === "nonzero_balance" ? (
                    <>
                      {t("partners.archive.blocker.nonzero_balance")}{" "}
                      <MoneyDisplay value={blocker.balance} />
                    </>
                  ) : (
                    t(`partners.archive.blocker.${blocker.code}`, { count: blocker.count })
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="size-4 text-success" aria-hidden="true" />
              {t("partners.archive.canArchive")}
            </p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="partners-archive-reason">{t("partners.archive.reason")}</Label>
              <Select
                value={reason}
                onValueChange={(value) => setReason(value as ArchiveReasonCode)}
              >
                <SelectTrigger id="partners-archive-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ARCHIVE_REASON_CODES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {t(`partners.archiveReason.${code}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("actions.close")}
          </Button>
          {!blocked && !guardError ? (
            <Button
              type="button"
              variant="destructive"
              disabled={checking}
              loading={archiving}
              onClick={() => onConfirm(reason)}
            >
              {t("partners.archive.confirm")}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface RestoreDialogProps {
  open: boolean;
  partyName: string;
  pending: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

export function RestoreDialog({
  open,
  partyName,
  pending,
  onConfirm,
  onOpenChange,
}: RestoreDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t("actions.close")}>
        <DialogHeader>
          <DialogTitle>{t("partners.archive.restoreTitle", { name: partyName })}</DialogTitle>
          <DialogDescription>{t("partners.archive.restoreBody")}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("actions.cancel")}
          </Button>
          <Button type="button" loading={pending} onClick={onConfirm}>
            {t("partners.archive.restoreConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
